import React, { useState, useRef, useEffect } from 'react';
import {
    Box, Typography, TextField, IconButton, Paper, List, ListItem, ListItemText,
    AppBar, Toolbar, Button, CircularProgress, Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

interface Message {
    role: 'user' | 'model';
    text: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const AIAssistantPage: React.FC = () => {
    const navigate = useNavigate();
    const [messages, setMessages] = useState<Message[]>([
        { role: 'model', text: '¡Hola! Soy tu asistente médico. ¿En qué puedo ayudarte hoy?' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim()) return;
        
        setError(null);
        const userMessage: Message = { role: 'user', text: input };
        const modelMessage: Message = { role: 'model', text: '' };
        setMessages(prev => [...prev, userMessage, modelMessage]);
        
        const currentInput = input;
        setInput('');
        setIsLoading(true);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await fetch(`${API_URL}/api/gemini`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ prompt: currentInput }),
            });

            if (!response.ok) {
                if (response.status === 401) {
                    navigate('/login');
                    return;
                }
                const errorData = await response.json();
                throw new Error(errorData.error || `Error: ${response.statusText}`);
            }

            const data = await response.json();
            
            setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage && lastMessage.role === 'model') {
                    lastMessage.text = data.response || data.text || "Sin respuesta.";
                }
                return newMessages;
            });
            
        } catch (error: any) {
            console.error(error);
            setError(error.message);
            setMessages(prev => {
                const newMessages = [...prev];
                const last = newMessages[newMessages.length - 1];
                if (last.role === 'model') last.text = "Error de conexión. Intente nuevamente.";
                return newMessages;
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <AppBar position="static" color="primary">
                <Toolbar>
                    {/* ✅ MEJORA: Botón "Volver" visible con texto */}
                    <Button
                        variant="contained"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        sx={{
                            mr: 2,
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 'bold',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.3)',
                            }
                        }}
                    >
                        VOLVER
                    </Button>
                    
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Asistente Médico IA
                    </Typography>
                </Toolbar>
            </AppBar>
            
            {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
            
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2, overflow: 'hidden' }}>
                <Paper sx={{ flexGrow: 1, p: 2, overflowY: 'auto', bgcolor: '#f5f5f5' }}>
                    <List>
                        {messages.map((msg, index) => (
                            <ListItem key={index} sx={{ justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                <Paper sx={{
                                    p: 2,
                                    backgroundColor: msg.role === 'user' ? 'primary.main' : 'white',
                                    color: msg.role === 'user' ? 'white' : 'text.primary',
                                    borderRadius: '20px',
                                    maxWidth: '80%',
                                }}>
                                    <ListItemText primary={msg.text} sx={{ whiteSpace: 'pre-wrap' }} />
                                </Paper>
                            </ListItem>
                        ))}
                        {isLoading && <ListItem><CircularProgress size={24} /></ListItem>}
                        <div ref={messagesEndRef} />
                    </List>
                </Paper>
                <Box sx={{ display: 'flex', p: 2, gap: 1, alignItems: 'center' }}>
                    <TextField 
                        fullWidth variant="outlined" placeholder="Escribe tu consulta..." 
                        value={input} onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                        disabled={isLoading}
                    />
                    <IconButton color="primary" onClick={handleSend} disabled={!input.trim() || isLoading}>
                        <SendIcon />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default AIAssistantPage;